import { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import {
  LEVEL_COLORS,
  CHART_HEIGHT,
  CHART_STYLES,
  BAR_ANIMATION_DURATION,
  MIN_BAR_WIDTH,
} from '../../../utils/constants';
import { TimeOfDayOccupancyRating } from '../types';
import { getColorForPercent } from './peakPersonalizationHelpers';
import styles from './PeakPersonalizationChart.scss';

export interface PeakPersonalizationChartDailyProps {
  data: Omit<TimeOfDayOccupancyRating, 'id'>[];
  labels: { field: string; headerName: string }[];
  onDataChange: (time: string, rating: number) => void;
  instruction: string;
  onClearAll: () => void;
  scale?: { min: number; max: number };
  valueLevels?: number[];
}

const HOLD_DELAY = 150;
const MOVE_THRESHOLD = 8;
const MOMENTUM_FRICTION = 0.9;
const EDGE_ZONE = 60;
const MAX_AUTO_SCROLL_SPEED = 8;

export const PeakPersonalizationChartDaily = ({
  data,
  labels,
  onDataChange,
  instruction,
  onClearAll,
  scale = { min: 0, max: 100 },
  valueLevels,
}: PeakPersonalizationChartDailyProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const touchModeRef = useRef<'undecided' | 'scroll' | 'drag'>('undecided');
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchOriginRef = useRef<{ x: number; y: number; scrollLeft: number }>({
    x: 0,
    y: 0,
    scrollLeft: 0,
  });
  const lastTouchRef = useRef({ x: 0, time: 0 });
  const currentTouchRef = useRef({ x: 0, y: 0 });
  const scrollVelocityRef = useRef(0);
  const momentumRef = useRef(0);
  const autoScrollRef = useRef(0);
  const [dimensions, setDimensions] = useState({ width: 0, innerWidth: 0 });

  const scaleRange = scale.max - scale.min;
  const innerHeight = CHART_HEIGHT - CHART_STYLES.top * 2;

  const getBarLayout = useCallback(
    (width: number) => {
      const n = labels.length;
      const step = Math.max(n > 0 ? width / n : 0, MIN_BAR_WIDTH);
      const innerWidth = n > 0 ? n * step : width;
      const bandwidth = step;

      return {
        innerWidth,
        bandwidth,
        step,
        getBarX: (i: number) => CHART_STYLES.left + i * step,
        getBarCenter: (i: number) => CHART_STYLES.left + (i + 0.5) * step,
      };
    },
    [labels.length],
  );

  const updateDimensions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const width = container.clientWidth;
    const innerWidth = width - CHART_STYLES.borderWidth;
    setDimensions({ width, innerWidth });
  }, []);

  useEffect(() => {
    updateDimensions();
    const container = containerRef.current;
    if (!container) return;
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [updateDimensions]);

  const getBarHeight = useCallback(
    (d: number, i: number) => {
      if (d <= scale.min) return 0;
      const yScale = (val: number) =>
        scaleRange > 0
          ? innerHeight - ((val - scale.min) / scaleRange) * innerHeight
          : innerHeight;
      return innerHeight - yScale(d);
    },
    [scale, scaleRange, innerHeight],
  );

  const chartAreaTopOffset = CHART_STYLES.top * 4;

  const getPositionFromMouse = useCallback(
    (
      clientX: number,
      clientY: number,
    ): { index: number; value: number } | null => {
      const container = containerRef.current;
      if (!container) return null;
      const rect = container.getBoundingClientRect();
      const x = clientX - rect.left - CHART_STYLES.left + container.scrollLeft;
      const y = clientY - rect.top - chartAreaTopOffset;
      const layout = getBarLayout(container.clientWidth);
      const { step, bandwidth } = layout;

      let index: number | null = null;
      for (let i = 0; i < labels.length; i++) {
        const bandStart = i * step;
        const bandEnd = bandStart + bandwidth;
        if (x >= bandStart && x <= bandEnd) {
          index = i;
          break;
        }
      }
      if (index === null) return null;
      const normalizedHeight = Math.max(0, Math.min(1, 1 - y / innerHeight));
      let value =
        normalizedHeight >= 0.95
          ? scale.max
          : scale.min + normalizedHeight * scaleRange;
      if (valueLevels && valueLevels.length > 0) {
        const nearest = valueLevels.reduce((prev, curr) =>
          Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
        );
        value = nearest;
      }
      return { index, value };
    },
    [labels, scale, scaleRange, valueLevels, getBarLayout, innerHeight],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const result = getPositionFromMouse(e.clientX, e.clientY);
      if (result !== null) {
        isDraggingRef.current = true;
        onDataChange(labels[result.index].field!, result.value);
      }
    },
    [getPositionFromMouse, onDataChange],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDraggingRef.current) return;
      const result = getPositionFromMouse(e.clientX, e.clientY);
      if (result !== null) {
        onDataChange(labels[result.index].field!, result.value);
      }
    },
    [getPositionFromMouse, onDataChange],
  );

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const stopMomentum = useCallback(() => {
    if (momentumRef.current) {
      cancelAnimationFrame(momentumRef.current);
      momentumRef.current = 0;
    }
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = 0;
    }
  }, []);

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const resetTouchState = useCallback(() => {
    clearHoldTimer();
    stopAutoScroll();
    isDraggingRef.current = false;
    touchModeRef.current = 'undecided';
  }, [clearHoldTimer, stopAutoScroll]);

  const startMomentum = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    let velocity = scrollVelocityRef.current * 16;
    if (Math.abs(velocity) < 0.5) return;

    const step = () => {
      velocity *= MOMENTUM_FRICTION;
      container.scrollLeft -= velocity;
      if (Math.abs(velocity) > 0.5) {
        momentumRef.current = requestAnimationFrame(step);
      } else {
        momentumRef.current = 0;
      }
    };
    momentumRef.current = requestAnimationFrame(step);
  }, []);

  const startAutoScroll = useCallback(() => {
    if (autoScrollRef.current) return;

    const tick = () => {
      const container = containerRef.current;
      if (!container || touchModeRef.current !== 'drag') {
        autoScrollRef.current = 0;
        return;
      }

      const rect = container.getBoundingClientRect();
      const relX = currentTouchRef.current.x - rect.left;
      let speed = 0;

      if (relX < EDGE_ZONE) {
        speed = -MAX_AUTO_SCROLL_SPEED * (1 - relX / EDGE_ZONE);
      } else if (relX > rect.width - EDGE_ZONE) {
        speed = MAX_AUTO_SCROLL_SPEED * (1 - (rect.width - relX) / EDGE_ZONE);
      }

      if (speed !== 0) {
        container.scrollLeft += speed;
        const result = getPositionFromMouse(
          currentTouchRef.current.x,
          currentTouchRef.current.y,
        );
        if (result) {
          onDataChange(labels[result.index].field!, result.value);
        }
        autoScrollRef.current = requestAnimationFrame(tick);
      } else {
        autoScrollRef.current = 0;
      }
    };

    autoScrollRef.current = requestAnimationFrame(tick);
  }, [getPositionFromMouse, onDataChange, labels]);

  const updateAutoScroll = useCallback(
    (clientX: number) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const relX = clientX - rect.left;
      const nearEdge = relX < EDGE_ZONE || relX > rect.width - EDGE_ZONE;

      if (nearEdge) {
        startAutoScroll();
      } else {
        stopAutoScroll();
      }
    },
    [startAutoScroll, stopAutoScroll],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;

      stopMomentum();
      resetTouchState();

      touchOriginRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        scrollLeft: containerRef.current?.scrollLeft ?? 0,
      };
      currentTouchRef.current = { x: touch.clientX, y: touch.clientY };
      scrollVelocityRef.current = 0;
      lastTouchRef.current = { x: touch.clientX, time: Date.now() };

      holdTimerRef.current = setTimeout(() => {
        holdTimerRef.current = null;
        if (touchModeRef.current !== 'undecided') return;
        touchModeRef.current = 'drag';
        isDraggingRef.current = true;
        const result = getPositionFromMouse(
          currentTouchRef.current.x,
          currentTouchRef.current.y,
        );
        if (result) {
          onDataChange(labels[result.index].field!, result.value);
        }
      }, HOLD_DELAY);
    },
    [getPositionFromMouse, onDataChange, labels, stopMomentum, resetTouchState],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      const origin = touchOriginRef.current;
      currentTouchRef.current = { x: touch.clientX, y: touch.clientY };

      if (touchModeRef.current === 'undecided') {
        const dx = Math.abs(touch.clientX - origin.x);
        const dy = Math.abs(touch.clientY - origin.y);
        if (dx < MOVE_THRESHOLD && dy < MOVE_THRESHOLD) return;
        clearHoldTimer();
        touchModeRef.current = 'scroll';
      }

      if (touchModeRef.current === 'scroll') {
        const container = containerRef.current;
        if (container) {
          container.scrollLeft = origin.scrollLeft - (touch.clientX - origin.x);
          const now = Date.now();
          const dt = now - lastTouchRef.current.time;
          if (dt > 0) {
            scrollVelocityRef.current =
              (touch.clientX - lastTouchRef.current.x) / dt;
          }
          lastTouchRef.current = { x: touch.clientX, time: now };
        }
      } else if (touchModeRef.current === 'drag') {
        const result = getPositionFromMouse(touch.clientX, touch.clientY);
        if (result) {
          onDataChange(labels[result.index].field!, result.value);
        }
        updateAutoScroll(touch.clientX);
      }
    },
    [
      getPositionFromMouse,
      onDataChange,
      labels,
      clearHoldTimer,
      updateAutoScroll,
    ],
  );

  const handleTouchEnd = useCallback(() => {
    if (touchModeRef.current === 'undecided') {
      clearHoldTimer();
      const result = getPositionFromMouse(
        touchOriginRef.current.x,
        touchOriginRef.current.y,
      );
      if (result) {
        onDataChange(labels[result.index].field!, result.value);
      }
    }

    if (touchModeRef.current === 'scroll') {
      const timeSinceLastMove = Date.now() - lastTouchRef.current.time;
      if (timeSinceLastMove < 100) {
        startMomentum();
      }
    }

    resetTouchState();
  }, [
    getPositionFromMouse,
    onDataChange,
    labels,
    startMomentum,
    resetTouchState,
    clearHoldTimer,
  ]);

  const handleTouchCancel = useCallback(() => {
    resetTouchState();
    stopMomentum();
  }, [resetTouchState, stopMomentum]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      isDraggingRef.current = false;
    };
    const handleGlobalTouchEnd = () => resetTouchState();
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalTouchEnd);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [resetTouchState]);

  useEffect(() => {
    return () => {
      stopMomentum();
      stopAutoScroll();
      clearHoldTimer();
    };
  }, [stopMomentum, stopAutoScroll, clearHoldTimer]);

  const layout = getBarLayout(dimensions.width);
  const hasDimensions = dimensions.width > 0;

  return (
    <div className={styles.chartSection}>
      <p className={styles.instruction}>{instruction}</p>
      <div
        ref={containerRef}
        className={cx(styles.chartContainer, styles.chartContainerScrollable)}
      >
        <div
          role="application"
          aria-label="Busy level chart - click and drag to set values"
          style={{ minWidth: '100%', width: layout.innerWidth }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        >
          <div
            className={styles.labelsRow}
            style={{
              width: layout.innerWidth,
              height: CHART_STYLES.top * 3,
              position: 'relative',
            }}
          >
            {labels.map((label) => (
              <div
                key={label.field!}
                className={`${styles.label} ${styles.labelRotated}`}
              >
                {label.headerName!}
              </div>
            ))}
          </div>
          {hasDimensions && (
            <div
              className={styles.chartContent}
              style={{
                paddingLeft: CHART_STYLES.left,
                paddingTop: CHART_STYLES.top,
              }}
            >
              <div
                className={styles.barsRow}
                style={{
                  width: layout.innerWidth,
                  height: innerHeight,
                }}
              >
                {labels.map((label, i) => {
                  const fieldRating =
                    data.find((value) => value.time === label.field)?.rating ??
                    0;
                  const barHeight = getBarHeight(fieldRating, i);
                  const normalizedPercent =
                    scaleRange > 0
                      ? ((fieldRating - scale.min) / scaleRange) * 100
                      : 0;
                  const showTopBorder: number =
                    normalizedPercent > 2 && normalizedPercent <= 95
                      ? 1
                      : normalizedPercent > 95
                        ? 0
                        : 0.25;
                  const barFill = getColorForPercent(normalizedPercent);
                  const barOpacity = fieldRating <= scale.min ? 0 : 1;

                  return (
                    <div
                      key={labels[i].field!}
                      className={styles.barColumn}
                      data-time={labels[i].field}
                    >
                      <div
                        className={styles.barDisabledBg}
                        style={{
                          backgroundColor: 'transparent',
                          cursor: 'pointer',
                        }}
                      />
                      <div className={styles.barWrapper}>
                        <div
                          className={styles.bar}
                          style={{
                            height: barHeight,
                            backgroundColor: barFill,
                            opacity: barOpacity,
                            transition: `height ${BAR_ANIMATION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1), opacity ${BAR_ANIMATION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1)`,
                          }}
                        />
                        <div
                          className={styles.barTopBorder}
                          style={{
                            bottom: barHeight,
                            opacity: showTopBorder,
                            transition: `bottom ${BAR_ANIMATION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1), opacity ${BAR_ANIMATION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1)`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.chartFooter}>
        <div className={styles.legend}>
          {LEVEL_COLORS.map((level) => (
            <div key={level.label} className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{
                  backgroundColor: level.color,
                }}
              />
              <span className={styles.legendLabel}>{level.label}</span>
            </div>
          ))}
        </div>
        <button type="button" className={styles.clearAll} onClick={onClearAll}>
          Clear all
        </button>
      </div>
    </div>
  );
};
